import { useState, useRef, useEffect } from 'react';
import { Globe, Check, Sparkles, Loader2 } from 'lucide-react';
import { useI18n, LOCALES, AI_LOCALES, englishTable } from '../i18n';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

/**
 * Compact globe-icon button that opens a dropdown of languages.
 * Built-in locales + an AI-translated section covering any language.
 * Variant: 'floating' = fixed top-right (only on /login & /register), 'inline' = inline button.
 */
export default function LanguageSwitcher({ variant = 'inline', className = '' }) {
    const { locale, setLocale, applyAiLocale } = useI18n();
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [translating, setTranslating] = useState(null);
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const persist = async (code) => {
        // Persist to localStorage synchronously so a later AuthContext.refresh()
        // (which compares server locale to localStorage) doesn't revert the choice.
        try { localStorage.setItem('locale', code); } catch { /* ignore */ }
        if (user) {
            try { await api.patch('/auth/me', { locale: code }); } catch { /* non-blocking */ }
        }
    };

    const change = async (code) => {
        setLocale(code);
        setOpen(false);
        await persist(code);
    };

    const changeAi = async (l) => {
        setOpen(false);
        try {
            const cached = localStorage.getItem('gd_ai_dict_' + l.code);
            if (cached) {
                applyAiLocale(l.code, JSON.parse(cached));
                await persist(l.code);
                return;
            }
        } catch { /* fall through to fetch */ }
        setTranslating(l.code);
        const tid = toast.loading(`Translating GrowDaily to ${l.label}… (first time only)`);
        try {
            const r = await api.post('/i18n/ui-translate', { lang: l.code, strings: englishTable() });
            applyAiLocale(l.code, r.data?.strings || {});
            await persist(l.code);
            toast.success(`${l.flag} ${l.label} ready`, { id: tid });
        } catch (err) {
            const st = err?.response?.status;
            const d = err?.response?.data?.detail;
            if (st === 401) toast.error('Log ind eller opret en gratis profil for at oversætte appen med AI 🌱', { id: tid });
            else if (typeof d === 'string') toast.error(d, { id: tid });
            else toast.error('Translation failed — please try again', { id: tid });
        } finally { setTranslating(null); }
    };

    const current = LOCALES.find((l) => l.code === locale) || AI_LOCALES.find((l) => l.code === locale) || LOCALES[0];

    const base = variant === 'floating'
        ? 'fixed top-4 right-4 z-[60]'
        : 'relative z-[60]';

    return (
        <div ref={ref} className={`${base} ${className}`}>
            <button
                data-testid="lang-switcher-btn"
                onClick={() => setOpen((o) => !o)}
                aria-label="Language"
                className="inline-flex items-center gap-2 bg-brand-card border border-brand-border rounded-full pl-3 pr-4 py-2 hover:shadow-sm transition-shadow"
            >
                <Globe size={15} strokeWidth={1.8} className="text-brand-green" />
                <span className="text-sm font-medium text-brand-ink">{current.flag}</span>
                <span className="text-xs text-brand-ink2 hidden sm:inline">{current.label}</span>
            </button>
            {open && (
                <div
                    data-testid="lang-switcher-menu"
                    className="absolute right-0 mt-2 min-w-[220px] max-h-[65vh] overflow-y-auto bg-brand-card border border-brand-border rounded-2xl shadow-xl z-[70] animate-fade-up"
                >
                    {LOCALES.map((l) => (
                        <button
                            key={l.code}
                            data-testid={`lang-${l.code}`}
                            onClick={() => change(l.code)}
                            className={`flex items-center justify-between w-full px-4 py-2.5 text-sm hover:bg-brand-bg2 transition-colors ${l.code === locale ? 'bg-brand-bg2' : ''}`}
                        >
                            <span className="flex items-center gap-2">
                                <span className="text-base">{l.flag}</span>
                                <span className="text-brand-ink">{l.label}</span>
                            </span>
                            {l.code === locale && <Check size={14} className="text-brand-green" strokeWidth={2} />}
                        </button>
                    ))}
                    <div className="px-4 pt-3 pb-1.5 border-t border-brand-border sticky top-0" data-testid="ai-lang-section">
                        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold text-brand-ochre">
                            <Sparkles size={11} /> AI-translated
                        </div>
                        <p className="text-[10px] text-brand-ink3 mt-0.5 leading-snug">Garden advice still follows your local climate &amp; location.</p>
                    </div>
                    {AI_LOCALES.map((l) => (
                        <button
                            key={l.code}
                            data-testid={`lang-ai-${l.code}`}
                            onClick={() => changeAi(l)}
                            disabled={!!translating}
                            className={`flex items-center justify-between w-full px-4 py-2.5 text-sm hover:bg-brand-bg2 transition-colors disabled:opacity-50 ${l.code === locale ? 'bg-brand-bg2' : ''}`}
                        >
                            <span className="flex items-center gap-2">
                                <span className="text-base">{l.flag}</span>
                                <span className="text-brand-ink">{l.label}</span>
                            </span>
                            {translating === l.code ? <Loader2 size={14} className="animate-spin text-brand-ochre" />
                                : l.code === locale ? <Check size={14} className="text-brand-green" strokeWidth={2} /> : null}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
